import type { ReactNode } from 'react';
import { Avatar, Divider } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import {
  WORKBOARD_ACCOUNT_SETTINGS_URL,
  WORKBOARD_ASSIGNMENT_RULES_URL,
  WORKBOARD_AUDIT_LOGS_URL,
} from 'dashboard409a/common/config/urls';
import { RootStateType } from 'dashboard409a/states/store';
import { useLogOutUser } from 'pages/Auth/hooks';
import { getInitials } from 'utilities/getInitials';

interface WorkboardOptionsMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

interface MenuSection {
  dividerBefore?: boolean;
  items: ReactNode[];
  key: string;
}

const renderMenuSections = (sections: MenuSection[]) =>
  sections.flatMap(section => {
    if (section.items.length === 0) return [];
    return [
      ...(section.dividerBefore ? [<Divider key={`${section.key}-divider`} />] : []),
      ...section.items,
    ];
  });

const WorkboardOptionsMenu = ({ anchorEl, onClose }: WorkboardOptionsMenuProps) => {
  const navigate = useNavigate();
  const logOutUser = useLogOutUser();
  const user = useSelector((state: RootStateType) => state.user);
  const currentCompany = useSelector((state: RootStateType) => state.global.currentCompany);

  const isStaffOrSuperuser = Boolean(user?.is_staff || user?.is_superuser);
  const isSuperuser = Boolean(user?.is_superuser);

  const handleNavigate = (url: string) => {
    navigate(url);
    onClose();
  };

  const handleSignOut = () => {
    onClose();
    logOutUser();
  };

  return (
    <PrimaryNavigationMenu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      menuMinWidth={200}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      {renderMenuSections([
        {
          key: 'company-header',
          items: currentCompany ? [
            <PrimaryNavigationMenuItem
              key="company-header"
              disabled
              isHeader
              leadingSlot={(
                <Avatar
                  alt={currentCompany.name}
                  src={currentCompany.icon_url ?? undefined}
                  variant="rounded"
                  sx={{ width: 16, height: 16, borderRadius: '4px', fontSize: 7 }}>
                  {getInitials({ name: currentCompany.name })}
                </Avatar>
              )}>
              {currentCompany.name}
            </PrimaryNavigationMenuItem>,
          ] : [],
        },
        {
          key: 'account-settings',
          items: [
            <PrimaryNavigationMenuItem
              key="account-settings"
              onClick={() => handleNavigate(WORKBOARD_ACCOUNT_SETTINGS_URL)}>
              Account Settings
            </PrimaryNavigationMenuItem>,
          ],
        },
        {
          key: 'audit-logs',
          items: isStaffOrSuperuser ? [
            <PrimaryNavigationMenuItem
              key="audit-logs"
              onClick={() => handleNavigate(WORKBOARD_AUDIT_LOGS_URL)}>
              Audit Logs
            </PrimaryNavigationMenuItem>,
          ] : [],
        },
        {
          key: 'assignment-rules',
          items: isSuperuser ? [
            <PrimaryNavigationMenuItem
              key="assignment-rules"
              onClick={() => handleNavigate(WORKBOARD_ASSIGNMENT_RULES_URL)}>
              Assignment Rules
            </PrimaryNavigationMenuItem>,
          ] : [],
        },
        {
          key: 'sign-out',
          dividerBefore: true,
          items: [
            <PrimaryNavigationMenuItem
              key="sign-out"
              onClick={handleSignOut}>
              Sign Out
            </PrimaryNavigationMenuItem>,
          ],
        },
      ])}
    </PrimaryNavigationMenu>
  );
};

export default WorkboardOptionsMenu;
